/**
 * Splash Screen Component
 *
 * カメラ許可前のオンボーディング画面。
 * 「ポスターにスマホをかざしてください」の案内と開始ボタンを表示し、
 * ユーザーがボタンをタップして初めて後続のカメラ許可フローに進む。
 *
 * ブラウザの許可ダイアログをいきなり出して「なぜカメラ？」と困惑させないための
 * 前捌き。デザインはプロジェクト共通の白黒シンプル系（黒背景 + 白文字 +
 * 白角丸ボタン）に準拠。
 *
 * @example
 * const splash = new SplashScreen();
 * splash.init(document.body);
 * splash.show();
 * await splash.waitForStart(); // ボタンタップを待つ
 * // ここでカメラ許可フローへ
 * splash.hide(0);
 */

const MAIN_INSTRUCTION = '対象物にスマホをかざしてください';
const SUB_INSTRUCTION = 'Hold your phone over the target';
const START_BUTTON_LABEL = '体験を始める';

export class SplashScreen {
  constructor() {
    this.container = null;
    this.startButton = null;
    this.isVisible = false;
    this.startResolvers = [];
    this.isStarted = false;
  }

  /**
   * スプラッシュを構築して親要素にマウント
   * @param {HTMLElement} parentElement
   * @param {Object} [options]
   * @param {string|null} [options.mainInstruction] - 案内文の差し替え。null で非表示
   *   （マーカーレス体験は「対象物にかざす」が成立しないため案内ごと出さない）
   * @param {string|null} [options.subInstruction]
   */
  init(
    parentElement,
    { mainInstruction = MAIN_INSTRUCTION, subInstruction = SUB_INSTRUCTION } = {}
  ) {
    this.container = document.createElement('div');
    this.container.className = 'splash-screen';
    this.container.style.display = 'none';

    const content = document.createElement('div');
    content.className = 'splash-content';

    if (mainInstruction) {
      const main = document.createElement('div');
      main.className = 'splash-instruction-main';
      main.textContent = mainInstruction;
      content.appendChild(main);
    }

    if (subInstruction) {
      const sub = document.createElement('div');
      sub.className = 'splash-instruction-sub';
      sub.textContent = subInstruction;
      content.appendChild(sub);
    }

    this.startButton = document.createElement('button');
    this.startButton.className = 'splash-start-button';
    this.startButton.type = 'button';
    this.startButton.textContent = START_BUTTON_LABEL;
    this.startButton.addEventListener('click', () => this._handleStart());

    content.appendChild(this.startButton);
    this.container.appendChild(content);

    parentElement.appendChild(this.container);

    console.log('SplashScreen initialized');
  }

  show() {
    if (this.container) {
      this.container.style.display = 'flex';
      this.isVisible = true;
    }
  }

  /**
   * @param {number} delay - フェードアウト開始までの遅延（ms）
   */
  hide(delay = 300) {
    if (!this.container) return;

    setTimeout(() => {
      this.container.classList.add('splash-fade-out');
      setTimeout(() => {
        if (this.container) {
          this.container.style.display = 'none';
        }
        this.isVisible = false;
      }, 500);
    }, delay);
  }

  /**
   * 開始ボタンのタップを待つ。すでにタップ済みなら即解決する。
   * @returns {Promise<void>}
   */
  waitForStart() {
    if (this.isStarted) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.startResolvers.push(resolve);
    });
  }

  /**
   * @private
   */
  _handleStart() {
    if (this.isStarted) return;
    this.isStarted = true;
    const resolvers = this.startResolvers.slice();
    this.startResolvers = [];
    resolvers.forEach((resolve) => resolve());
  }

  cleanup() {
    if (this.container && this.container.parentElement) {
      this.container.parentElement.removeChild(this.container);
    }
    this.container = null;
    this.startButton = null;
    this.startResolvers = [];
    console.log('SplashScreen cleaned up');
  }
}
